import NavigationService from '@disco/state-management/NavigationService'
import type { Playlist } from 'types/playlist'
import type { GetState } from 'types/redux'
import type { Track } from 'types/track'
import {
  loadBatch,
  mapResponseToReducerObjects,
} from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import {
  BULK_DESELECT_ALL,
  BULK_DESELECT_TRACK,
  BULK_SELECT_ALL,
  BULK_SELECT_TRACK,
  TRACK_METADATA_FETCH,
  TRACK_METADATA_FETCH_ERROR,
  TRACK_METADATA_FETCH_SUCCESS,
} from '@disco/constants/ActionTypes'
import { getNestedTracks } from '@disco/utils/tracks'
/**
 * Fetches all tracks for a playlist for bulk operations, including nested tracks.
 * Filters out tracks that are outside user's territories and handles errors.
 * @param playlist - The playlist to fetch tracks from
 * @returns Promise that resolves to an object with tracks array or null/undefined on error
 */
export function bulkGetTracksForPlaylist(playlist: Playlist): (
  dispatch: any,
  getState: GetState,
) => Promise<
  | {
      tracks: Array<Track>
    }
  | null
  | undefined
> {
  return async (
    dispatch: (...args: Array<any>) => any,
    getState: GetState,
  ): Promise<
    | {
        tracks: Track[]
      }
    | null
    | undefined
  > => {
    try {
      dispatch({
        type: TRACK_METADATA_FETCH,
        bulk: true,
        fetchingTracks: [],
      })
      const response = await api.getFullPlaylist(playlist)
      // Get the nested tracks
      const nestedTracks = await getNestedTracks(response.tracks)
      // Store the nested tracks in the reducer
      dispatch(loadBatch(nestedTracks))
      const fullPlaylist = mapResponseToReducerObjects(
        dispatch,
        getState,
        response,
      )
      const reducerTracks = getState().tracks
      const tracks: Track[] = fullPlaylist.tracks
        .filter(t => !reducerTracks[t].outside_your_territories)
        .map(t => reducerTracks[t])

      if (tracks.length === 0) {
        dispatch({
          type: TRACK_METADATA_FETCH_ERROR,
          bulk: true,
          error: new Error(
            'Tracks are not available for editing, possibly due to territory restrictions.',
          ),
        })
        NavigationService.push('/')
        return
      }

      const mergedTracks = tracks.concat(nestedTracks)
      dispatch({
        type: TRACK_METADATA_FETCH_SUCCESS,
        bulk: true,
        fetchingTracks: mergedTracks,
        tracksMetadata: mergedTracks.map(t => t.metadata),
      })
      return {
        tracks: mergedTracks,
      }
    } catch (error) {
      dispatch({
        type: TRACK_METADATA_FETCH_ERROR,
        bulk: true,
        error,
      })
    }
  }
}
/**
 * Selects a track for bulk operations.
 * @param track - The track to select
 * @param index - The index of the track in the list
 * @returns Action object with BULK_SELECT_TRACK type, track, and index
 */
export function bulkSelectTrack(
  track: Track,
  index: number,
): {
  index: number
  track: Track
  type: string
} {
  return {
    type: BULK_SELECT_TRACK,
    track,
    index,
  }
}
/**
 * Deselects a track from bulk operations.
 * @param track - The track to deselect
 * @param index - The index of the track in the list
 * @returns Action object with BULK_DESELECT_TRACK type, track, and index
 */
export function bulkDeselectTrack(
  track: Track,
  index: number,
): {
  index: number
  track: Track
  type: string
} {
  return {
    type: BULK_DESELECT_TRACK,
    track,
    index,
  }
}
/**
 * Selects all tracks for bulk operations.
 * @returns Action object with BULK_SELECT_ALL type
 */
export function bulkSelectAll(): {
  type: string
} {
  return {
    type: BULK_SELECT_ALL,
  }
}
/**
 * Deselects all tracks from bulk operations.
 * @returns Action object with BULK_DESELECT_ALL type
 */
export function bulkDeselectAll(): {
  type: string
} {
  return {
    type: BULK_DESELECT_ALL,
  }
} // TODO: move other actions from BulkEditor
